import { motion } from 'framer-motion'
import { 
  ArrowLeft, 
  LogOut,
  Scissors,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  BarChart3,
  Heart,
  Star,
  Calendar,
  DollarSign,
  Sparkles,
  User,
  Camera,
  Gift,
  ShoppingBag,
  Palette,
  Brain,
  Target,
  Activity
} from 'lucide-react'
import InfoTooltip from '../common/InfoTooltip'

export default function SalonDashboard({ onBack, onLogout, businessData }) {
  const kpis = [
    {
      title: 'Satisfacción Cliente',
      value: '96%',
      change: '+8%',
      trend: 'up',
      icon: Heart,
      color: 'pink',
      tooltip: {
        title: 'Índice de Satisfacción del Cliente',
        description: 'Medición integral de la satisfacción basada en encuestas post-servicio, tiempo de espera y calidad percibida.',
        aiMethod: 'Análisis de sentimientos en feedback, scoring automático de experiencias, predicción de satisfacción.',
        dataSource: 'Encuestas digitales, tiempos de servicio, historial de citas, feedback verbal transcrito.',
        updateFrequency: 'Actualización en tiempo real tras cada servicio'
      }
    },
    {
      title: 'Aumento Ventas',
      value: '35%',
      change: '+12%',
      trend: 'up',
      icon: TrendingUp,
      color: 'green',
      description: 'vs mes anterior',
      tooltip: {
        title: 'Incremento en Ventas por IA',
        description: 'Porcentaje de aumento en ventas atribuible a recomendaciones personalizadas y optimización de servicios con IA.',
        aiMethod: 'Algoritmos de recomendación personalizada, análisis de patrones de compra, optimización de precios.',
        dataSource: 'Histórico de ventas, perfiles de clientes, servicios utilizados, productos comprados.',
        updateFrequency: 'Cálculo diario con consolidación mensual'
      }
    },
    {
      title: 'Retención Clientes',
      value: '88%',
      change: '+5%',
      trend: 'up',
      icon: Users,
      color: 'blue',
      tooltip: {
        title: 'Tasa de Retención de Clientes',
        description: 'Porcentaje de clientes que regresan al salón, mejorado mediante personalización y seguimiento inteligente.',
        aiMethod: 'Predicción de churn, personalización de ofertas, alertas de seguimiento automático.',
        dataSource: 'Historial de citas, frecuencia de visitas, patrones de comportamiento, feedback de clientes.',
        updateFrequency: 'Análisis semanal con predicciones continuas'
      }
    },
    {
      title: 'Recomendaciones IA',
      value: '247',
      change: '+89',
      trend: 'up',
      icon: Sparkles,
      color: 'purple',
      suffix: '/día',
      tooltip: {
        title: 'Recomendaciones Inteligentes Generadas',
        description: 'Número de recomendaciones personalizadas generadas por IA para servicios, productos y tratamientos.',
        aiMethod: 'ML para análisis de preferencias, recomendaciones colaborativas, personalización basada en tipo de cabello/piel.',
        dataSource: 'Historial de servicios, preferencias declaradas, resultados de tratamientos, fotos de antes/después.',
        updateFrequency: 'Generación instantánea con cada interacción'
      }
    }
  ]

  const personalizedServices = [
    {
      service: 'Análisis de Piel',
      aiAccuracy: 94,
      recommendations: 156,
      satisfaction: 97,
      revenue: '+28%',
      color: 'pink'
    },
    {
      service: 'Colorimetría IA',
      aiAccuracy: 91,
      recommendations: 134,
      satisfaction: 93,
      revenue: '+42%',
      color: 'purple'
    },
    {
      service: 'Tratamientos Faciales',
      aiAccuracy: 96,
      recommendations: 98,
      satisfaction: 95,
      revenue: '+31%',
      color: 'blue'
    },
    {
      service: 'Estilo Personalizado',
      aiAccuracy: 89,
      recommendations: 87,
      satisfaction: 92,
      revenue: '+25%',
      color: 'green'
    }
  ]

  const clientInsights = [
    {
      name: 'María García',
      visits: 12,
      preference: 'Tratamientos naturales',
      recommendation: 'Mascarilla de colágeno',
      satisfaction: 98,
      nextAppointment: '15 Jun',
      avatar: '👩🏻'
    },
    {
      name: 'Ana Rodríguez',
      visits: 8,
      preference: 'Coloración',
      recommendation: 'Mechas californianas',
      satisfaction: 95,
      nextAppointment: '17 Jun',
      avatar: '👩🏽'
    },
    {
      name: 'Sofia Méndez',
      visits: 15,
      preference: 'Cortes modernos',
      recommendation: 'Bob asimétrico',
      satisfaction: 99,
      nextAppointment: '20 Jun',
      avatar: '👩🏻‍🦱'
    }
  ]

  const trendingServices = [
    { name: 'Keratina Brasileña', bookings: 45, growth: '+67%', rating: 4.9 },
    { name: 'Balayage IA', bookings: 38, growth: '+89%', rating: 4.8 },
    { name: 'Tratamiento Anti-edad', bookings: 29, growth: '+34%', rating: 4.7 },
    { name: 'Manicure Francesa', bookings: 52, growth: '+23%', rating: 4.6 },
    { name: 'Maquillaje de Novia', bookings: 18, growth: '+156%', rating: 5.0 }
  ]

  const aiRecommendations = [
    {
      type: 'inventory',
      message: 'Stock bajo: Shampoo Biosilk - Reordenar 12 unidades',
      priority: 'high',
      time: 'Hace 2h'
    },
    {
      type: 'appointment',
      message: 'Cliente VIP disponible para cita premium el viernes',
      priority: 'medium',
      time: 'Hace 4h'
    },
    {
      type: 'marketing',
      message: 'Promoción de tratamientos faciales aumentaría ventas 23%',
      priority: 'low',
      time: 'Hace 6h'
    }
  ]

  const monthlyRevenue = [
    { month: 'Ene', traditional: 45000, ai_optimized: 52000 },
    { month: 'Feb', traditional: 48000, ai_optimized: 58000 },
    { month: 'Mar', traditional: 46000, ai_optimized: 61000 },
    { month: 'Abr', traditional: 49000, ai_optimized: 65000 },
    { month: 'May', traditional: 51000, ai_optimized: 69000 },
    { month: 'Jun', traditional: 50000, ai_optimized: 73000 }
  ]

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      purple: 'bg-purple-100 text-purple-800',
      pink: 'bg-pink-100 text-pink-800',
      orange: 'bg-orange-100 text-orange-800'
    }
    return colors[color] || colors.blue
  }

  const getIconColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      pink: 'bg-pink-500',
      orange: 'bg-orange-500'
    }
    return colors[color] || colors.blue
  }

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800'
    }
    return colors[priority] || colors.medium
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </button>
              <div className="flex items-center">
                <div className={`w-8 h-8 bg-gradient-to-br ${businessData.color} rounded-lg flex items-center justify-center mr-3`}>
                  <Scissors className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{businessData.title}</h1>
                  <p className="text-sm text-gray-500">Beauty Analytics con IA</p>
                </div>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* KPIs Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {kpis.map((kpi, index) => {
            const IconComponent = kpi.icon
            return (
              <div key={index} className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm text-gray-600">{kpi.title}</p>
                      <InfoTooltip 
                        title={kpi.tooltip.title}
                        description={kpi.tooltip.description}
                        aiMethod={kpi.tooltip.aiMethod}
                        dataSource={kpi.tooltip.dataSource}
                        updateFrequency={kpi.tooltip.updateFrequency}
                        iconColor="text-gray-400"
                      />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {kpi.value}{kpi.suffix || ''}
                    </p>
                    <div className="flex items-center mt-2">
                      <span className={`text-sm font-medium ${kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                        {kpi.change}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">
                        {kpi.description || 'vs mes anterior'}
                      </span>
                    </div>
                  </div>
                  <div className={`p-3 ${getIconColorClasses(kpi.color)} rounded-lg`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Servicios Personalizados IA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-semibold text-gray-900">Servicios Personalizados IA</h3>
                  <InfoTooltip 
                    title="Motor de Personalización de Servicios de Belleza"
                    description="Sistema de IA que analiza características individuales para recomendar tratamientos y servicios personalizados óptimos."
                    aiMethod="Análisis de imagen por computer vision, ML para tipos de piel/cabello, algoritmos de recomendación colaborativa."
                    dataSource="Fotos de clientes, historial de servicios, resultados de tratamientos, preferencias declaradas."
                    updateFrequency="Análisis inmediato con cada nueva visita"
                    iconColor="text-gray-400"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Brain className="w-5 h-5 text-pink-600" />
                  <span className="text-sm text-gray-600">Beauty Engine</span>
                </div>
              </div>
              
              <div className="space-y-6">
                {personalizedServices.map((service, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{service.service}</h4>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getColorClasses(service.color)}`}>
                        {service.aiAccuracy}% precisión
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-sm text-gray-600">Recomendaciones</p>
                        <p className="text-lg font-bold text-gray-900">{service.recommendations}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Satisfacción</p>
                        <p className="text-lg font-bold text-gray-900">{service.satisfaction}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Ingresos</p>
                        <p className="text-lg font-bold text-green-600">{service.revenue}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Precisión IA</p>
                        <div className="mt-1">
                          <div className="bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${getIconColorClasses(service.color)}`}
                              style={{ width: `${service.aiAccuracy}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Análisis de Ingresos */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Ingresos: Tradicional vs Optimización IA
                </h3>
                <InfoTooltip 
                  title="Comparativo de Ingresos con Optimización IA"
                  description="Análisis comparativo de ingresos generados usando métodos tradicionales versus optimización con inteligencia artificial."
                  aiMethod="Optimización de precios dinámicos, recomendaciones de upselling, análisis de demanda, personalización de ofertas."
                  dataSource="Histórico de ventas, patrones de demanda, perfiles de clientes, análisis de competencia."
                  updateFrequency="Optimización diaria con análisis mensual comparativo"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {monthlyRevenue.map((month, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="w-12 text-sm font-medium text-gray-700">{month.month}</div>
                    
                    <div className="flex-1 flex items-center space-x-4">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">Tradicional</span>
                          <span className="text-sm font-medium">${month.traditional.toLocaleString()}</span>
                        </div>
                        <div className="bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-gray-400"
                            style={{ width: `${(month.traditional / 80000) * 100}%` }}
                          />
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">Con IA</span>
                          <span className="text-sm font-medium">${month.ai_optimized.toLocaleString()}</span>
                        </div>
                        <div className="bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-pink-500"
                            style={{ width: `${(month.ai_optimized / 80000) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <span className="text-sm font-medium text-green-600">
                        +{Math.round(((month.ai_optimized - month.traditional) / month.traditional) * 100)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-pink-50 rounded-lg">
                <p className="text-sm text-pink-800">
                  <strong>Impacto total:</strong> +$138,000 MXN en ingresos adicionales en 6 meses 
                  gracias a recomendaciones personalizadas de IA.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Insights de Clientes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Perfiles de Clientes IA</h3>
                <InfoTooltip 
                  title="Análisis Inteligente de Perfiles de Cliente"
                  description="Creación automática de perfiles detallados de clientes con recomendaciones personalizadas basadas en análisis de IA."
                  aiMethod="Análisis de patrones de comportamiento, segmentación automática, predicción de preferencias, scoring de satisfacción."
                  dataSource="Historial de servicios, feedback de clientes, fotos de resultados, patrones de visita."
                  updateFrequency="Actualización automática después de cada visita"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {clientInsights.map((client, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="text-2xl">{client.avatar}</div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{client.name}</h4>
                        <p className="text-sm text-gray-600">{client.visits} visitas</p>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span className="text-sm font-medium">{client.satisfaction}%</span>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="text-sm">
                        <span className="text-gray-600">Preferencia:</span>
                        <span className="ml-1 font-medium">{client.preference}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-600">Recomendación IA:</span>
                        <span className="ml-1 font-medium text-pink-600">{client.recommendation}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-600">Próxima cita:</span>
                        <span className="ml-1 font-medium">{client.nextAppointment}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Servicios Trending */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Servicios en Tendencia</h3>
                <InfoTooltip 
                  title="Análisis de Tendencias de Servicios"
                  description="Identificación automática de servicios en auge basado en patrones de reserva, satisfacción y crecimiento de demanda."
                  aiMethod="Análisis de series temporales, detección de patrones de demanda, predicción de tendencias estacionales."
                  dataSource="Datos de reservas, rating de servicios, frecuencia de solicitud, análisis de mercado local."
                  updateFrequency="Análisis semanal con actualizaciones de tendencias"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-3">
                {trendingServices.map((service, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 text-sm">{service.name}</h4>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-xs text-gray-600">{service.bookings} reservas</span>
                        <span className="text-xs font-medium text-green-600">{service.growth}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-sm font-medium">{service.rating}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Recomendaciones IA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Recomendaciones IA</h3>
                <InfoTooltip 
                  title="Motor de Recomendaciones Inteligentes"
                  description="Sistema que genera recomendaciones automáticas para optimizar inventario, marketing y estrategias de crecimiento del salón."
                  aiMethod="Machine learning para análisis predictivo, optimización de recursos, análisis de rentabilidad por servicio."
                  dataSource="Datos de ventas, inventario, patrones estacionales, análisis de competencia, feedback de clientes."
                  updateFrequency="Recomendaciones actualizadas diariamente"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-3">
                {aiRecommendations.map((rec, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-3">
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-full flex-shrink-0 ${getPriorityColor(rec.priority)}`}>
                        {rec.type === 'inventory' && <ShoppingBag className="w-4 h-4" />}
                        {rec.type === 'appointment' && <Calendar className="w-4 h-4" />}
                        {rec.type === 'marketing' && <Target className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{rec.message}</p>
                        <p className="text-xs text-gray-500 mt-1">{rec.time}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Próximas Tareas */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Agenda Inteligente</h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-pink-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Palette className="w-4 h-4 text-pink-600" />
                    <span className="text-sm font-medium">Análisis de piel - 5 clientes</span>
                  </div>
                  <span className="text-xs text-pink-600">Hoy 14:00</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Camera className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium">Sesión de fotos - Antes/Después</span>
                  </div>
                  <span className="text-xs text-purple-600">Mañana 10:00</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Gift className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium">Programa de lealtad - Review</span>
                  </div>
                  <span className="text-xs text-blue-600">Viernes</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  )
} 